import { ISelectOption } from '../../components/form/fields/select.component';
import { countryList } from '../../json/countryList';

/**
 * Countries kept at the top of the address country dropdown, in this order, as in the
 * legacy Windows client. Everything else follows sorted by its localized name.
 */
const PINNED_CODES = ['CH', 'DE', 'AT', 'GB', 'MV', 'RU', 'AE'];

/**
 * Codes CLDR resolves to their successor country (`AN` -> Curacao, `CS` -> Serbia).
 * `shared/json/countries.ts` is an older ISO snapshot that still carries them and does
 * not carry the successors, so localizing them would offer a name the persisted code
 * contradicts - they keep their original English label instead.
 */
const WITHDRAWN_ISO_CODES = new Set(['AN', 'CS']);

const displayNamesCache = new Map<string, Intl.DisplayNames>();

const getRegionDisplayNames = (language: string): Intl.DisplayNames => {
  let displayNames = displayNamesCache.get(language);
  if (!displayNames) {
    // `fallback: 'none'` returns undefined instead of echoing the code back, so an
    // unknown code falls through to the English name below.
    displayNames = new Intl.DisplayNames([language], { type: 'region', fallback: 'none' });
    displayNamesCache.set(language, displayNames);
  }
  return displayNames;
};

/**
 * Localized country name for an ISO 3166-1 alpha-2 code, falling back to `fallbackName`
 * (and then to the code itself) when the code is unknown to the runtime.
 */
export const localizedCountryName = (
  code: string | null | undefined,
  language: string,
  fallbackName?: string,
): string => {
  if (!code) return fallbackName ?? '';
  try {
    const displayNames = getRegionDisplayNames(language);
    const name = displayNames.of(code.toUpperCase());
    // CLDR names the reserved code `ZZ` ("Unknown Region"); never show that to a user.
    if (name && name !== displayNames.of('ZZ')) return name;
  } catch {
    /* invalid code - fall through */
  }
  return fallbackName ?? code;
};

/**
 * Options for the customer address country field: the `id` stays the canonical English
 * upper-case name persisted in `Land`/`LieferscheinLand` (see `json/countryList.ts` - the
 * printed address and ZUGFeRD match on it), only the label is localized.
 */
export const getCountryOptions = (language: string): ISelectOption[] => {
  const collator = new Intl.Collator(language);
  const pinnedByCode = new Map<string, ISelectOption>();
  const rest: ISelectOption[] = [];

  for (const { name, code } of countryList) {
    const option = { id: name, label: localizedCountryName(code, language, name) };
    if (code && PINNED_CODES.includes(code)) pinnedByCode.set(code, option);
    else rest.push(option);
  }
  rest.sort((a, b) => collator.compare(a.label, b.label));

  const pinned = PINNED_CODES.map((code) => pinnedByCode.get(code)).filter(
    (option) => option !== undefined,
  );
  return [...pinned, ...rest];
};

/**
 * Localizes the labels of options whose `id` is an ISO 3166-1 alpha-2 code (the one-time
 * customer popups keep storing the code), preserving the incoming order.
 */
export const localizeIsoCountryOptions = (
  options: ISelectOption[],
  language: string,
): ISelectOption[] =>
  options.map((option) =>
    WITHDRAWN_ISO_CODES.has(String(option.id).toUpperCase())
      ? option
      : { ...option, label: localizedCountryName(String(option.id), language, option.label) },
  );
